import filesize from "filesize";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import type { FileViolation } from "../../../../shared/core/data-forms/file-constraints";
import { base64ByteSize } from "../../../../shared/core/data-forms/file-constraints";
import type { FormFileInput } from "../../../../shared/core/data-forms/form-input-types";
import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { describeDocument } from "../../../../shared/domains/onboarding/document-cache";
import type { OnboardingDocument } from "../../../../shared/domains/onboarding/onboarding";
import { isDefined } from "../../../../shared/utils/assert";
import { DEFAULT_FILE_UPLOADER_TYPES, FileUploader } from "../forms/file-uploader";
import { openDocument } from "./open-document";

export const FileField: React.FC<{
  input: FormFileInput;
  onChange: (picture: string | null) => void;
  onViolations?: (violations: FileViolation[]) => void;
  disabled?: boolean;
}> = ({ input, onChange, onViolations, disabled }) => {
  const { formatMessage, formatDate } = useIntl();
  // Only what was picked here can be opened: a file already sent lives on the server, and its
  // contents were never brought back.
  const [pickedFile, setPickedFile] = useState<File | null>(null);
  // Surtout pas `document` : dans un navigateur, ce nom est déjà celui du DOM.
  const [sentDocument, setSentDocument] = useState<OnboardingDocument | undefined>();

  const isDictInputValue = input.value && typeof input.value === "object";

  const hasExistingFile = useMemo(() => {
    if (typeof input.value === "object") {
      return isDefined(input.value?.documentId);
    }
    return false;
  }, [input.value]);

  const documentId = useMemo(
    () => (hasExistingFile ? (input.value as { documentId: string }).documentId : undefined),
    [hasExistingFile, input.value],
  );

  // Le serveur ne renvoie que l'identifiant ; la date d'envoi se demande, une fois.
  useEffect(() => {
    if (!documentId) {
      setSentDocument(undefined);
      return;
    }

    let abandoned = false;
    describeDocument(documentId)
      .then((described) => !abandoned && setSentDocument(described))
      .catch(() => undefined);

    return () => {
      abandoned = true;
    };
  }, [documentId]);

  const onPreview = useCallback(() => {
    if (pickedFile) {
      window.open(URL.createObjectURL(pickedFile), "_blank", "noopener");
      return;
    }
    if (documentId) {
      openDocument(documentId).catch(() => undefined);
    }
  }, [documentId, pickedFile]);

  return (
    <InputContainer>
      <FileUploader
        initialFile={
          input.value && !isDictInputValue
            ? {
                name: formatMessage("billFormScreen.docInputFilled"),
                type: "image/png",
                size: filesize(base64ByteSize(input.value as string)),
              }
            : hasExistingFile
              ? {
                  name: sentDocument
                    ? formatMessage("common.fileField.sentOn", {
                        date: formatDate(new Date(sentDocument.uploadDate), {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        }),
                      })
                    : formatMessage("common.fileField.sent"),
                  type: "",
                  size: "",
                }
              : undefined
        }
        hasExistingFile={hasExistingFile}
        onViolations={onViolations}
        typeAllowed={input.acceptedTypes || DEFAULT_FILE_UPLOADER_TYPES}
        onPreviewClicked={pickedFile || documentId ? onPreview : undefined}
        fileUploaded={async (file) => {
          setPickedFile(file);
          if (!file) {
            onChange(null);
            return;
          }
          const reader = new FileReader();

          reader.addEventListener(
            "load",
            () => {
              // convert image file to base64 string
              const base64Image = reader.result?.toString() ?? null;
              onChange(base64Image?.split("base64,")?.[1] ?? null);
            },
            false,
          );

          if (file) {
            reader.readAsDataURL(file);
          }
        }}
        instructions={formatMessage("billFormScreen.docInputEmpty")}
        disabled={disabled}
        maxSize={input.maxSize}
        required={input.required}
        showDownloadIcon
      />
    </InputContainer>
  );
};

const InputContainer = styled.div`
  position: relative;
  flex: 1;
`;
